import { create } from "zustand";
import {
  getCycledBrushEntry,
  getCycledDecorationBrushEntry,
  getDecorationBrushEntryPreviewDecorationId,
  getDecorationPaintSetById,
  getDefaultBrushEntry,
  getDefaultDecorationBrushEntry,
  getOrderedBrushEntries,
  getOrderedDecorationBrushEntries,
  getPaintSetIdForDecoration,
  getTileSetById,
  isDynamicTileSet,
  type DecorationBrushEntry,
  type OrderedBrushEntry,
} from "@west-marches/shared";
import {
  EMPTY_EDITOR_PERSISTENCE_STATE,
  type EditorPersistenceState,
  type HistoryEntry,
} from "@west-marches/shared";

const MAX_HISTORY = 50;
export const MIN_DECORATION_SCATTER_RADIUS = 24;
export const MAX_DECORATION_SCATTER_RADIUS = 168;
export const DEFAULT_DECORATION_SCATTER_RADIUS = 96;
export const DECORATION_SCATTER_DENSITY_STEPS = [1, 2, 3, 4, 5] as const;
export const MIN_DECORATION_SCATTER_DENSITY = DECORATION_SCATTER_DENSITY_STEPS[0];
export const MAX_DECORATION_SCATTER_DENSITY =
  DECORATION_SCATTER_DENSITY_STEPS[DECORATION_SCATTER_DENSITY_STEPS.length - 1];
export const DEFAULT_DECORATION_SCATTER_DENSITY = 3;

function clampDecorationScatterRadius(radius: number): number {
  return Math.min(
    MAX_DECORATION_SCATTER_RADIUS,
    Math.max(MIN_DECORATION_SCATTER_RADIUS, Math.round(radius)),
  );
}

function clampDecorationScatterDensity(density: number): number {
  const roundedDensity = Math.round(density);
  let closestStep: number = DECORATION_SCATTER_DENSITY_STEPS[0];
  let smallestDistance = Math.abs(roundedDensity - closestStep);

  for (const step of DECORATION_SCATTER_DENSITY_STEPS) {
    const distance = Math.abs(roundedDensity - step);
    if (distance < smallestDistance) {
      smallestDistance = distance;
      closestStep = step;
    }
  }

  return closestStep;
}

export type Tool =
  | "pointer"
  | "tile-brush"
  | "decoration-brush"
  | "eyedropper"
  | "road"
  | "map-note"
  | "text";

export function isBrushTool(tool: Tool): tool is "tile-brush" | "decoration-brush" {
  return tool === "tile-brush" || tool === "decoration-brush";
}

export type DecorationMode = "single" | "brush" | "erase";
export type EditorMode = "paint" | "enrich";
export type ViewAsRole = "actual" | "player";
export type LastUsedBrush =
  | { kind: "tile"; tileSetId: string }
  | { kind: "decoration"; decorationId: string; taxonomyNodeId: string | null };

const VIEW_AS_STORAGE_PREFIX = "wm.viewAs:";

function viewAsStorageKey(campaignId: string): string {
  return `${VIEW_AS_STORAGE_PREFIX}${campaignId}`;
}

function loadViewAsForCampaign(campaignId: string | null): ViewAsRole {
  if (!campaignId || typeof window === "undefined") return "actual";
  try {
    const value = window.sessionStorage.getItem(viewAsStorageKey(campaignId));
    return value === "player" ? "player" : "actual";
  } catch {
    return "actual";
  }
}

function persistViewAsForCampaign(campaignId: string | null, viewAs: ViewAsRole): void {
  if (!campaignId || typeof window === "undefined") return;
  try {
    if (viewAs === "actual") {
      window.sessionStorage.removeItem(viewAsStorageKey(campaignId));
    } else {
      window.sessionStorage.setItem(viewAsStorageKey(campaignId), viewAs);
    }
  } catch {
    // sessionStorage may be unavailable (private mode); preview just won't persist across reloads
  }
}

interface EditorStore {
  selectedTileSetId: string | null;
  selectedBrushEntry: OrderedBrushEntry | null;
  /**
   * Canonical decoration selection: the active overlay paint set. Mirrors
   * `selectedTileSetId` for the tile brush.
   */
  selectedDecorationPaintSetId: string | null;
  /**
   * Currently-highlighted entry within `selectedDecorationPaintSetId`. For
   * single-asset (singleton) sets this is the one decoration entry; for
   * multi-entry ordered sets, Q/E cycles it; for dynamic sets it stays null
   * because each stroke randomizes per placement.
   */
  selectedDecorationBrushEntry: DecorationBrushEntry | null;
  /**
   * Resolved overlay id derived from the brush entry. Maintained for backward
   * compatibility with consumers that just need "which decoration is currently
   * targeted by the cursor preview". Updated automatically when the brush
   * entry changes; `null` whenever no decoration paint set is selected.
   */
  selectedDecorationId: string | null;
  selectedDecorationTaxonomyNodeId: string | null;
  decorationMode: DecorationMode;
  decorationScatterRadius: number;
  decorationScatterDensity: number;
  selectedTaxonomyNodeId: string | null;
  activeTool: Tool;
  editorMode: EditorMode;
  canEdit: boolean;
  activeCampaignId: string | null;
  viewAs: ViewAsRole;
  lastUsedBrush: LastUsedBrush | null;
  history: HistoryEntry[];
  historyIndex: number;
  selectTaxonomyNode: (taxonomyNodeId: string) => void;
  selectBrush: (tileSetId: string, tileId?: string | null) => void;
  selectDecoration: (decorationId: string, taxonomyNodeId?: string | null) => void;
  selectDecorationPaintSet: (
    paintSetId: string,
    taxonomyNodeId?: string | null,
    initialEntry?: DecorationBrushEntry | null,
  ) => void;
  clearSelectedDecoration: () => void;
  setDecorationMode: (mode: DecorationMode) => void;
  setDecorationScatterRadius: (radius: number) => void;
  setDecorationScatterDensity: (density: number) => void;
  setSelectedBrushEntry: (entry: OrderedBrushEntry | null) => void;
  cycleSelectedBrushEntry: (step: number) => void;
  setSelectedDecorationBrushEntry: (entry: DecorationBrushEntry | null) => void;
  cycleSelectedDecorationBrushEntry: (step: number) => void;
  deselectBrush: () => void;
  setActiveTool: (tool: Tool) => void;
  setEditorMode: (mode: EditorMode) => void;
  setCanEdit: (canEdit: boolean) => void;
  setActiveCampaignId: (id: string | null) => void;
  setViewAs: (viewAs: ViewAsRole) => void;
  pushHistory: (entry: HistoryEntry) => void;
  restorePersistence: (state: Partial<EditorPersistenceState>) => void;
  clearHistory: () => void;
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
}

export const useEditorStore = create<EditorStore>((set, get) => ({
  selectedTileSetId: null,
  selectedBrushEntry: null,
  selectedDecorationPaintSetId: null,
  selectedDecorationBrushEntry: null,
  selectedDecorationId: null,
  selectedDecorationTaxonomyNodeId: null,
  decorationMode: "single",
  decorationScatterRadius: DEFAULT_DECORATION_SCATTER_RADIUS,
  decorationScatterDensity: DEFAULT_DECORATION_SCATTER_DENSITY,
  selectedTaxonomyNodeId: null,
  activeTool: "pointer",
  editorMode: "paint",
  canEdit: true,
  activeCampaignId: null,
  viewAs: "actual",
  lastUsedBrush: null,
  history: [],
  historyIndex: -1,

  selectTaxonomyNode(taxonomyNodeId) {
    set((s) => ({
      selectedTaxonomyNodeId:
        s.selectedTaxonomyNodeId === taxonomyNodeId ? null : taxonomyNodeId,
    }));
  },

  selectBrush(tileSetId, tileId = null) {
    set((s) => {
      const newId = s.selectedTileSetId === tileSetId ? null : tileSetId;
      const tileSet = newId ? getTileSetById(newId) : null;
      const selectedBrushEntry =
        newId && tileSet
          ? isDynamicTileSet(tileSet)
            ? null
            : tileId
              ? getOrderedBrushEntries(tileSet).find(
                  (entry) => entry.kind === "tile" && entry.tileId === tileId,
                ) ?? getDefaultBrushEntry(tileSet)
              : getDefaultBrushEntry(tileSet)
          : null;
      return {
        selectedTileSetId: newId,
        selectedBrushEntry,
        selectedDecorationPaintSetId: null,
        selectedDecorationBrushEntry: null,
        selectedDecorationId: null,
        selectedDecorationTaxonomyNodeId: null,
        decorationMode: "single",
        decorationScatterRadius: DEFAULT_DECORATION_SCATTER_RADIUS,
        decorationScatterDensity: DEFAULT_DECORATION_SCATTER_DENSITY,
        ...(newId ? { activeTool: "tile-brush" as Tool } : {}),
      };
    });
  },

  selectDecoration(decorationId, taxonomyNodeId = null) {
    const paintSetId = getPaintSetIdForDecoration(decorationId);
    if (!paintSetId) {
      // No paint set found for this decoration; fall back to a singleton-style
      // selection so legacy callers (and fixtures without paint set data) keep
      // working.
      set((state) => ({
        selectedTileSetId: null,
        selectedBrushEntry: null,
        selectedDecorationPaintSetId: null,
        selectedDecorationBrushEntry: { kind: "decoration", decorationId },
        selectedDecorationId: decorationId,
        selectedDecorationTaxonomyNodeId: taxonomyNodeId,
        decorationMode: "single" as DecorationMode,
        decorationScatterRadius:
          state.selectedDecorationId === null
            ? DEFAULT_DECORATION_SCATTER_RADIUS
            : state.decorationScatterRadius,
        decorationScatterDensity:
          state.selectedDecorationId === null
            ? DEFAULT_DECORATION_SCATTER_DENSITY
            : state.decorationScatterDensity,
        activeTool: "decoration-brush" as Tool,
      }));
      return;
    }

    get().selectDecorationPaintSet(paintSetId, taxonomyNodeId, {
      kind: "decoration",
      decorationId,
    });
  },

  selectDecorationPaintSet(paintSetId, taxonomyNodeId = null, initialEntry) {
    const paintSet = getDecorationPaintSetById(paintSetId);
    if (!paintSet) {
      return;
    }

    const entries = getOrderedDecorationBrushEntries(paintSet);
    const proposedEntry =
      initialEntry &&
      entries.some(
        (candidate) =>
          (candidate.kind === "decoration" &&
            initialEntry.kind === "decoration" &&
            candidate.decorationId === initialEntry.decorationId) ||
          (candidate.kind === "paint-set" &&
            initialEntry.kind === "paint-set" &&
            candidate.paintSetId === initialEntry.paintSetId),
      )
        ? initialEntry
        : getDefaultDecorationBrushEntry(paintSet);
    const previewDecorationId = getDecorationBrushEntryPreviewDecorationId(proposedEntry);

    set((state) => ({
      selectedTileSetId: null,
      selectedBrushEntry: null,
      selectedDecorationPaintSetId: paintSetId,
      selectedDecorationBrushEntry: proposedEntry,
      selectedDecorationId: previewDecorationId,
      selectedDecorationTaxonomyNodeId: taxonomyNodeId,
      decorationMode: "single" as DecorationMode,
      decorationScatterRadius:
        state.selectedDecorationPaintSetId === null
          ? DEFAULT_DECORATION_SCATTER_RADIUS
          : state.decorationScatterRadius,
      decorationScatterDensity:
        state.selectedDecorationPaintSetId === null
          ? DEFAULT_DECORATION_SCATTER_DENSITY
          : state.decorationScatterDensity,
      activeTool: "decoration-brush" as Tool,
    }));
  },

  clearSelectedDecoration() {
    const s = get();
    set({
      selectedDecorationPaintSetId: null,
      selectedDecorationBrushEntry: null,
      selectedDecorationId: null,
      selectedDecorationTaxonomyNodeId: null,
      decorationMode: "single",
      decorationScatterRadius: DEFAULT_DECORATION_SCATTER_RADIUS,
      decorationScatterDensity: DEFAULT_DECORATION_SCATTER_DENSITY,
      activeTool: "pointer",
      ...(s.selectedDecorationId
        ? {
            lastUsedBrush: {
              kind: "decoration" as const,
              decorationId: s.selectedDecorationId,
              taxonomyNodeId: s.selectedDecorationTaxonomyNodeId,
            },
          }
        : {}),
    });
  },

  setDecorationMode(mode) {
    if (!get().selectedDecorationId) {
      return;
    }
    set({ decorationMode: mode });
  },

  setDecorationScatterRadius(radius) {
    set({ decorationScatterRadius: clampDecorationScatterRadius(radius) });
  },

  setDecorationScatterDensity(density) {
    set({ decorationScatterDensity: clampDecorationScatterDensity(density) });
  },

  setSelectedBrushEntry(entry) {
    const tileSetId = get().selectedTileSetId;
    if (!tileSetId) {
      return;
    }

    const tileSet = getTileSetById(tileSetId);
    if (!tileSet || isDynamicTileSet(tileSet)) {
      return;
    }

    const nextEntry =
      entry &&
      getOrderedBrushEntries(tileSet).some((candidate) =>
        candidate.kind === "tile"
          ? entry.kind === "tile" && candidate.tileId === entry.tileId
          : entry.kind === "tile-set" && candidate.tileSetId === entry.tileSetId,
      )
        ? entry
        : getDefaultBrushEntry(tileSet);
    set({ selectedBrushEntry: nextEntry, activeTool: "tile-brush" });
  },

  cycleSelectedBrushEntry(step) {
    const tileSetId = get().selectedTileSetId;
    if (!tileSetId) {
      return;
    }

    const tileSet = getTileSetById(tileSetId);
    if (!tileSet || isDynamicTileSet(tileSet)) {
      return;
    }

    const nextEntry = getCycledBrushEntry(tileSet, get().selectedBrushEntry, step);
    set({ selectedBrushEntry: nextEntry, activeTool: "tile-brush" });
  },

  setSelectedDecorationBrushEntry(entry) {
    const paintSetId = get().selectedDecorationPaintSetId;
    if (!paintSetId) {
      return;
    }

    const paintSet = getDecorationPaintSetById(paintSetId);
    if (!paintSet) {
      return;
    }

    const nextEntry =
      entry &&
      getOrderedDecorationBrushEntries(paintSet).some(
        (candidate) =>
          (candidate.kind === "decoration" &&
            entry.kind === "decoration" &&
            candidate.decorationId === entry.decorationId) ||
          (candidate.kind === "paint-set" &&
            entry.kind === "paint-set" &&
            candidate.paintSetId === entry.paintSetId),
      )
        ? entry
        : getDefaultDecorationBrushEntry(paintSet);
    set({
      selectedDecorationBrushEntry: nextEntry,
      selectedDecorationId: getDecorationBrushEntryPreviewDecorationId(nextEntry),
      activeTool: "decoration-brush",
    });
  },

  cycleSelectedDecorationBrushEntry(step) {
    const paintSetId = get().selectedDecorationPaintSetId;
    if (!paintSetId) {
      return;
    }

    const paintSet = getDecorationPaintSetById(paintSetId);
    if (!paintSet) {
      return;
    }

    const nextEntry = getCycledDecorationBrushEntry(
      paintSet,
      get().selectedDecorationBrushEntry,
      step,
    );
    set({
      selectedDecorationBrushEntry: nextEntry,
      selectedDecorationId: getDecorationBrushEntryPreviewDecorationId(nextEntry),
      activeTool: "decoration-brush",
    });
  },

  deselectBrush() {
    set({ selectedTileSetId: null, selectedBrushEntry: null });
  },

  setActiveTool(tool) {
    const s = get();
    let lastUsedBrush = s.lastUsedBrush;

    if (s.selectedDecorationId) {
      lastUsedBrush = {
        kind: "decoration",
        decorationId: s.selectedDecorationId,
        taxonomyNodeId: s.selectedDecorationTaxonomyNodeId,
      };
    } else if (tool === "pointer" && s.selectedTileSetId) {
      lastUsedBrush = { kind: "tile", tileSetId: s.selectedTileSetId };
    }

    const editorModeForTool: EditorMode | null =
      tool === "map-note" || tool === "text"
        ? "enrich"
        : isBrushTool(tool) || tool === "eyedropper" || tool === "road"
          ? "paint"
          : null;

    set({
      activeTool: tool,
      selectedDecorationPaintSetId: null,
      selectedDecorationBrushEntry: null,
      selectedDecorationId: null,
      selectedDecorationTaxonomyNodeId: null,
      decorationMode: "single",
      decorationScatterRadius: DEFAULT_DECORATION_SCATTER_RADIUS,
      decorationScatterDensity: DEFAULT_DECORATION_SCATTER_DENSITY,
      lastUsedBrush,
      ...(tool === "pointer" ? { selectedTileSetId: null, selectedBrushEntry: null } : {}),
      ...(editorModeForTool ? { editorMode: editorModeForTool } : {}),
    });
  },

  setEditorMode(mode) {
    if (mode === "enrich") {
      const s = get();
      let lastUsedBrush = s.lastUsedBrush;

      if (s.selectedDecorationId) {
        lastUsedBrush = {
          kind: "decoration",
          decorationId: s.selectedDecorationId,
          taxonomyNodeId: s.selectedDecorationTaxonomyNodeId,
        };
      } else if (s.selectedTileSetId) {
        lastUsedBrush = { kind: "tile", tileSetId: s.selectedTileSetId };
      }

      set({
        editorMode: "enrich",
        activeTool: "map-note",
        selectedTileSetId: null,
        selectedBrushEntry: null,
        selectedDecorationPaintSetId: null,
        selectedDecorationBrushEntry: null,
        selectedDecorationId: null,
        selectedDecorationTaxonomyNodeId: null,
        decorationMode: "single",
        decorationScatterRadius: DEFAULT_DECORATION_SCATTER_RADIUS,
        decorationScatterDensity: DEFAULT_DECORATION_SCATTER_DENSITY,
        lastUsedBrush,
      });
    } else {
      set({
        editorMode: "paint",
        activeTool: "pointer",
      });
    }
  },

  setCanEdit(canEdit) {
    set({ canEdit });
  },

  setActiveCampaignId(id) {
    const previous = get().activeCampaignId;
    if (previous === id) {
      return;
    }
    set({
      activeCampaignId: id,
      viewAs: loadViewAsForCampaign(id),
    });
  },

  setViewAs(viewAs) {
    const campaignId = get().activeCampaignId;
    persistViewAsForCampaign(campaignId, viewAs);
    set({ viewAs });
  },

  pushHistory(entry) {
    set((s) => {
      const history = s.history.slice(0, s.historyIndex + 1);
      history.push(entry);
      if (history.length > MAX_HISTORY) {
        history.shift();
      }
      return { history, historyIndex: history.length - 1 };
    });
  },

  restorePersistence(state) {
    const history = Array.isArray(state.history)
      ? state.history.slice(-MAX_HISTORY)
      : EMPTY_EDITOR_PERSISTENCE_STATE.history;
    const maxIndex = history.length - 1;
    const historyIndex =
      typeof state.historyIndex === "number"
        ? Math.min(Math.max(state.historyIndex, -1), maxIndex)
        : maxIndex;

    set({
      selectedTileSetId: null,
      selectedBrushEntry: null,
      selectedDecorationPaintSetId: null,
      selectedDecorationBrushEntry: null,
      selectedDecorationId: null,
      selectedDecorationTaxonomyNodeId: null,
      decorationMode: "single",
      decorationScatterRadius: DEFAULT_DECORATION_SCATTER_RADIUS,
      decorationScatterDensity: DEFAULT_DECORATION_SCATTER_DENSITY,
      selectedTaxonomyNodeId: null,
      activeTool: "pointer",
      lastUsedBrush: null,
      history,
      historyIndex,
    });
  },

  clearHistory() {
    set({
      history: EMPTY_EDITOR_PERSISTENCE_STATE.history,
      historyIndex: EMPTY_EDITOR_PERSISTENCE_STATE.historyIndex,
    });
  },

  undo() {
    set((s) => {
      if (s.historyIndex < 0) return s;
      return { historyIndex: s.historyIndex - 1 };
    });
  },

  redo() {
    set((s) => {
      if (s.historyIndex >= s.history.length - 1) return s;
      return { historyIndex: s.historyIndex + 1 };
    });
  },

  canUndo() {
    return get().historyIndex >= 0;
  },

  canRedo() {
    return get().historyIndex < get().history.length - 1;
  },
}));
